import { AddPrimitivesCommand, BasicControl, CURSOR_TYPE, DYMODE_MODE, Line, LineT, Listeners, MOUSE, PlineT, Point, PrimitiveColor, TnEngineContext, TnEngineExtraContext, ViewEditor, dymode, vector3AngleTo } from "pytha";
import { ARCHI_CTRL_TYPE } from "../enums/archibase-control-type";
import { Vector3 } from "three";
import { ARCHIBASE_SINGAL } from "../enums/archibase-singal";
import { getSpecialPrimitiveLayerUuid } from "@/commons/utils/layer-utils";
import { LayerType } from "@/commons/enums/layer-type";
import { RoofSlapT } from "../models/RoofSlapT";


const EXECUTE_TYPE = {
    POINT: 1, // 通过指定点来布置屋面板
}

/*
 * 布置屋面板二级控制器
 */
export class AssembleRoofSlapControl extends BasicControl {
    id = ARCHI_CTRL_TYPE.ASSEMBLE_ROOF_SLAP;

    needInit: boolean;  // 是否需要初始化数据

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    bottomFace: PlineT; // 屋面板截面

    executeType: number = EXECUTE_TYPE.POINT;  // 布置方式


    /*
     * 1：指定屋面板边沿起点
     * 2：指定屋面板边沿终点
     * 3：指定屋面板拉伸方向和拉伸长度
     */
    step: number = 1;

    firstPoint: Vector3 = new Vector3(); // 第一个点坐标
    secondPoint: Vector3 = new Vector3(); // 第二个点坐标
    thirdPoint: Vector3 = new Vector3(); // 第三个点坐标

    line: LineT = new LineT();  // 绘制时的临时边沿线
    edgeNormal: Vector3 = new Vector3(); // 边沿线的方向向量

    tempRoofSlaps: RoofSlapT[] = []; // 临时展示的屋面板

    capturePoint: Vector3;  // 捕捉点
    orthogonalPoint: Vector3; // 正交点
    polarPoint: Vector3; // 极轴追踪点

    point_down: Point = new Point(); // 鼠标按下坐标点
    point_move: Point = new Point(); // 鼠标移动坐标点

    projectUuid: string = ""
    modelUuid: string = ""

    // 构造函数
    constructor(editor: ViewEditor, listeners: Listeners) {
        super();
        this.needInit = true;

        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();

        this.editor = editor;
        this.listeners = listeners;
        this.editor.clampControl.enabled = false;
        this.listeners.getSignal(ARCHIBASE_SINGAL.onAssembleRoofSlapByPoint).add(this.onAssembleRoofSlapByPoint);

        console.log('AssembleRoofSlapControl added')
    }

    // 析构函数
    dispose(): void {
        this.listeners.getSignal(ARCHIBASE_SINGAL.onAssembleRoofSlapByPoint).remove(this.onAssembleRoofSlapByPoint);
        // 派发置空选择集的信号
        this.listeners.signals.emptySelectedEntities.dispatch();
        // 移除鼠标按下和鼠标移动的监听
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);
        this.listeners.signals.noNeedCapture.dispatch();
        this.editor.selectControl.setEnabled(true);
        this.extraContext.dymodeContext.enabled = false;
        this.editor.orthogonalControl.enabled = false;
        this.editor.selectControl.setEnabled(true);
        this.editor.clampControl.enabled = true;
        this.editor.clampControl.hidden = false;
        let obj = this.line?.viewObj;
        // 移除临时线
        if (obj && !!obj.parent) {
            obj.parent.remove(obj);
        }
        // 移除临时屋面板
        for (let i = 0; i < this.tempRoofSlaps.length; i++) {
            let obj = this.tempRoofSlaps[i]?.viewObj;
            if (obj && !!obj.parent) {
                obj.parent.remove(obj);
            }
        }
        console.log('AssembleRoofSlapControl removed')
    }

    // 初始化
    initControl(data: any): void {
        if (!!data.executeType) {
            if (data.executeType === EXECUTE_TYPE.POINT) {
                this.onAssembleRoofSlapByPoint(data.bottomFace, data.projectUuid, data.modelUuid);
            }
        }
    }

    /**
     * 通过选点来布置屋面板
     * @param {PlineT} bottomFace 屋面板截面
     */
    onAssembleRoofSlapByPoint = (bottomFace: PlineT, projectUuid: string, modelUuid: string) => {
        let initData = {
            bottomFace: bottomFace,
            executeType: EXECUTE_TYPE.POINT,
            projectUuid: projectUuid,
            modelUuid: modelUuid,
        }
        this.listeners.signals.setInitControlData.dispatch(initData);
        this.executeType = EXECUTE_TYPE.POINT;
        this.bottomFace = bottomFace;
        this.projectUuid = projectUuid
        this.modelUuid = modelUuid

        this.step = 1;
        this.editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        this.editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
        this.listeners.signals.needCapture.dispatch();
        this.listeners.signals.emptySelectedEntities.dispatch();
        this.listeners.signals.onPointCapture.add(this.onPointCapture);
        this.listeners.signals.onDymodeChanged.add(this.onDymodeChanged);
        this.listeners.signals.onPointOrthogonal.add(this.onPointOrthogonal);
        // 订阅极轴追踪信号，收听到极轴追踪点的坐标
        this.listeners.signals.onPointPolarTracking.add(this.onPointPolarTracking);
        this.editor.selectControl.setEnabled(false);
        this.extraContext.dymodeContext.enabled = true;
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.POINT);
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        this.listeners.signals.sendToDymodeTip.dispatch('指定边沿起点:');
        this.extraContext.stmodeContext.setStmode('指定边沿起点');
        this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.CROSS);
    }

    onPointPolarTracking = (point) => {
        this.polarPoint = point;
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    onPointOrthogonal = (point) => {
        this.orthogonalPoint = point;
    }

    // 鼠标按下
    onPointerDown = (event) => {
        if (event.button === MOUSE.RIGHT) {
            this.extraContext.listeners.signals.onOpeCommandControlEnd.dispatch();
        }
        if (event.button !== MOUSE.LEFT) return;
        let rect = this.editor.renderer.domElement.getBoundingClientRect();
        let x = (event.clientX - rect.x) / rect.width * 2 - 1;
        let y = -(event.clientY - rect.y) / rect.height * 2 + 1;
        this.context.ucsContext.unprojectCursorPoint(x, y, this.editor.camera, this.point_down);
        if (this.orthogonalPoint) {
            this.point_down.copy(this.orthogonalPoint);
        }
        if (this.capturePoint) {
            this.point_down.copy(this.capturePoint);
        }
        if (this.polarPoint) {
            this.point_down.copy(this.polarPoint);
        }
        if (this.step === 1) {
            // 获得了第一个点坐标
            this.firstPoint = this.point_down.clone();
            this.firstStep();
        } else if (this.step === 2) {
            // 获得了第二个点坐标
            this.secondPoint = this.point_down.clone();
            this.secondStep();
        } else if (this.step === 3) {
            // 获得了第三个点坐标
            this.thirdPoint = this.point_down.clone();
            this.thirdStep();
        }
    }

    // 鼠标移动
    onPointerMove = (event) => {
        if (this.step === 1) return;
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.orthogonalPoint) {
            this.point_move.copy(this.orthogonalPoint);
        }
        if (this.polarPoint) {
            this.point_move.copy(this.polarPoint);
        }
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        }
        if (this.step === 2) {
            // 更新临时线
            this.line.setValue('end', this.point_move);
            this.line.updateViewGeometry();
            this.line.updateViewMaterial();
            this.listeners.signals.needRender.dispatch();
        }
        if (this.step === 3) {
            this._dir.copy(this.point_move).sub(this.secondPoint)
            if (this._dir.length() < 1e-6) {
                return
            }

            // 获取拉伸平面的normal
            let planeNormal = this.edgeNormal.clone().cross(this._dir.clone().normalize())
            // 获取拉伸方向
            let extVector = planeNormal.cross(this.edgeNormal)
            // 获取在拉伸方向上的投影长度
            let extLength = this._dir.dot(extVector.clone()) / extVector.length()
            // 获取旋转角度
            let angle = vector3AngleTo(this.tempRoofSlaps[0]._geoSurface.direction, extVector.clone(), this.edgeNormal) || 0
            this.tempRoofSlaps.forEach(tempRoofSlap => {
                if (Math.abs(angle) > 1e-7) {
                    tempRoofSlap._geoSurface.rotate(tempRoofSlap._geoSurface.baseCurve[0].getPointAt(0), this.edgeNormal, angle);
                }
                tempRoofSlap._geoSurface.height = extLength
                tempRoofSlap.updateViewGeometry();
            })
            this.listeners.signals.needRender.dispatch();
        }
    }

    // 指定了边沿起点
    firstStep = () => {
        this.line.set(this.firstPoint, this.firstPoint);
        this.line.color.set(PrimitiveColor.editColor);
        this.listeners.signals.needCapture.dispatch(this.firstPoint);
        let view = this.line.render();
        this.extraContext.sceneContext.scene.add(view);
        this.listeners.signals.needRender.dispatch();
        this.step++;
        this.listeners.signals.sendToDymodeTip.dispatch('指定边沿终点:');
        this.extraContext.stmodeContext.setStmode('指定边沿终点');
        this.listeners.signals.orthogonal.dispatch(this.firstPoint);
        this.listeners.signals.polarTracking.dispatch(this.firstPoint);
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.DIST, this.firstPoint);
    }

    // 指定了边沿终点
    secondStep = () => {
        // 边沿线方向向量
        this.edgeNormal.copy(this.secondPoint).sub(this.firstPoint).normalize();
        // 移除临时线
        let obj = this.line?.viewObj;
        if (obj && !!obj.parent) {
            obj.parent.remove(obj);
        }
        // 生成临时屋面板图元
        let tempY = this.edgeNormal.clone().cross(this.context.ucsContext.getAxisList().axisZ).normalize();
        this.generateTempRoofSlaps(tempY, this.firstPoint, this.secondPoint, 10);
        this.step++;
        this.extraContext.stmodeContext.setStmode('指定波纹方向');
        this.listeners.signals.needCapture.dispatch(this.secondPoint);
        this.listeners.signals.orthogonal.dispatch(this.secondPoint);
        this.listeners.signals.polarTracking.dispatch(this.secondPoint);
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.DIST, this.secondPoint);
    }

    private _dir = new Vector3();
    // 指定了拉伸方向和拉伸长度
    thirdStep = () => {
        // 获取拉伸平面的normal
        let planeNormal = this.edgeNormal.clone().cross(this._dir.clone().normalize())
        // 获取拉伸方向
        let extVector = planeNormal.cross(this.edgeNormal)
        // 获取在拉伸方向上的投影长度
        let extLength = this._dir.dot(extVector.clone()) / extVector.length()
        // 获取旋转角度
        let angle = vector3AngleTo(this.tempRoofSlaps[0]._geoSurface.direction, extVector.clone(), this.edgeNormal) || 0
        this.tempRoofSlaps.forEach(tempRoofSlap => {
            if (Math.abs(angle) > 1e-7) {
                tempRoofSlap._geoSurface.rotate(tempRoofSlap._geoSurface.baseCurve[0].getPointAt(0), this.edgeNormal, angle);
            }
            tempRoofSlap._geoSurface.height = extLength
            tempRoofSlap.updateViewGeometry();
        })
        this.listeners.signals.needRender.dispatch();
        this.insertRoofSlab();
    }

    // 添加屋面板
    insertRoofSlab = () => {
        getSpecialPrimitiveLayerUuid(this.projectUuid, this.modelUuid, LayerType.ROOF_SLAB).then((layerUuid: string) => {
            this.editor.history.execute(new AddPrimitivesCommand(this.tempRoofSlaps.map(item => {
                item.layerUuid = layerUuid
                return (
                    item.clone()
                )
            })));
        }).finally(() => {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
        })

    }

    private _tmpPoint = new Point();

    // 将一个截面放置在指定点，并转角
    getSection = (section: PlineT, point: Point, normalRotateAxis: Vector3, normalDeltaAngle: number, axisXRotateAxis: Vector3, axisXDeltaAngle: number) => {
        this._dir.set(point.x - section.pointList[0].x, point.y - section.pointList[0].y, point.z - section.pointList[0].z);
        let moveLength = this._dir.length();
        this._dir.normalize();
        section.move(this._dir, moveLength);
        section.rotate(point, normalRotateAxis, normalDeltaAngle);
        section.rotate(point, axisXRotateAxis, axisXDeltaAngle);
        return section;
    }


    /**
     * 将屋面板截面按路径复制
     * @param section 屋面板截面
     * @param curve 路径曲线
     * @param space 相邻两个屋面板截面的间隔（参照的同一基准点，如果没有搭接，间隔为屋面板截面的长度）
     * @param normal 截面的法向量朝向
     * @param needEnd 终点处是否复制（当路径不能等分时）
     */
    copyByLine = (section: PlineT, curve: Line, space: number, normal: Vector3, needEnd: boolean = false) => {
        if (!section) return [];
        // 周长
        let distTotal = curve.distance();
        let axisZ = normal.clone();
        let normalRotateAxis = section._geoPlane.normal.clone().cross(axisZ).normalize();
        if (Math.abs(section._geoPlane.normal.dot(axisZ) + 1) < 1e-6) {
            normalRotateAxis = section.axisX.clone();
        }
        let normalDeltaAngle = axisZ.angleTo(section._geoPlane.normal);
        let axisX = curve.delta().projectOnPlane(normal).normalize();
        let tempAxisX = section.axisX.clone().applyAxisAngle(normalRotateAxis, normalDeltaAngle);
        let axisXRotateAxis = tempAxisX.clone().cross(axisX).normalize();
        let axisXDeltaAngle = tempAxisX.angleTo(axisX);
        if (Math.abs(tempAxisX.dot(axisX) + 1) < 1e-6) {
            axisXRotateAxis = axisZ.clone();
        }
        let newEntities = []
        // 端点
        let endPoint = curve.getPointAt(1);
        // 计算间距占周长的比例
        let lengthFactor = space / distTotal;
        // 最多可以分成几段
        let divNumber = Math.floor(distTotal / space);
        for (let i = 0; i <= divNumber; i++) {
            curve.getPointAt(i * lengthFactor, this._tmpPoint);
            if (!needEnd && this._tmpPoint.equals(endPoint)) {
                // 不布置端点
                continue;
            }
            // 重复间隔的界面
            newEntities.push(this.getSection(section.clone(), this._tmpPoint, normalRotateAxis, normalDeltaAngle, axisXRotateAxis, axisXDeltaAngle));
        }
        return newEntities;
    }


    /**
     * 生成临时的屋面板图元，沿屋面板边沿重复屋面板个数，沿拉伸方向拉伸
     * @param normal 拉伸方向
     * @param firstPoint 屋面板边沿起点
     * @param second 屋面板边沿终点
     * @param height 拉伸高度
     */
    generateTempRoofSlaps = (normal: Vector3 = null, firstPoint: Vector3, secondPoint: Vector3, height: number = 0) => {
        // 单个屋面板的包围盒
        let roofSlapSectionBox = this.bottomFace.getBoundingBox();
        // 长度
        let length = roofSlapSectionBox.max.x - roofSlapSectionBox.min.x;
        // 屋面板边沿
        let line = new Line(firstPoint, secondPoint);
        // 沿屋面板边沿重复屋面板截面
        let sections = this.copyByLine(this.bottomFace, line, length, normal, true);
        // 沿拉伸方向拉伸成屋面板
        sections.forEach(section => {
            let tempRoofSlap = new RoofSlapT(section._geoCurves, normal, height);
            this.tempRoofSlaps.push(tempRoofSlap);
            this.extraContext.sceneContext.scene.add(tempRoofSlap.render());
        })
        this.listeners.signals.needRender.dispatch();
    }

    _dymodeActive = false;

    // 动态输入值
    onDymodeChanged = (value: dymode) => {
        if (value.mode === DYMODE_MODE.POINT) {
            if (this.step == 1) {
                // 获得了第一个点
                this.firstPoint.set(value.x, value.y, value.z);
                this.firstStep();
            } else if (this.step == 2) {
                // 获得了第二个点
                this.secondPoint.set(value.x, value.y, value.z);
                this.secondStep();
            } else if (this.step == 3) {
                // 获得了第三个点
                this.thirdPoint.set(value.x, value.y, value.z);
                this.thirdStep();
            }
        } else if (value.mode === DYMODE_MODE.DIST) {
            if (this.step == 2) {
                this._dir = this.point_move.clone().sub(this.firstPoint).normalize();
                let end = this._dir.multiplyScalar(value.dist).add(this.firstPoint);
                this.secondPoint.set(end.x, end.y, end.z);
                this.secondStep();
            } else if (this.step == 3) {
                this._dir = this.point_move.clone().sub(this.secondPoint).normalize();
                let end = this._dir.multiplyScalar(value.dist).add(this.secondPoint);
                this.thirdPoint.set(end.x, end.y, end.z);
                this.thirdStep();
            }
        }
    }
}